/**
 * Player-only org invite entry: `/join?join=CODE`.
 *
 * Behavior:
 * - Signed-out → fetch org branding by join code, show TicketStub landing with
 *   "Join Now" / "Sign In" CTAs forwarding to `/sign-in?returnUrl=/join?join=CODE`.
 * - Signed-in → call the join API immediately, then redirect to:
 *   - `/app/worldcup/groups/{groupId}` when the org has a linked squad
 *   - `/app/worldcup/groups` otherwise
 * - On error → show a single message + "Back to app".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { useLocalSearchParams, useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext'
import { useTheme } from '../../src/theme/useTheme'
import { JoinFlowLoadingScreen } from '../../src/join/components/JoinFlowLoadingScreen'
import { JoinFlowScreen } from '../../src/join/components/JoinFlowScreen'
import { extractErrorMessage, isTransientServerError, useApiClient } from '../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../constants/api'
import { sanitizeJoinCodeInput } from '../../src/accounts/helpers/b2bAccount.helper'
import { buildPublicSignInHref } from '../../src/signIn/helpers/postAuthRedirect.helper'
import { getJoinDeepLinkStyles } from '../../appStyles/_joinDeepLink.styles'
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations'
import type { B2bJoinOrganizationResponse } from '../../src/accounts/types/b2bAccount.types'
import { logger } from '../../src/helpers/logger.helper'
import { OrgBrandedLanding } from '../../components/onboarding/OrgBrandedLanding/OrgBrandedLanding'
import {
  fetchPublicOrgByJoinCode,
  type PublicOrgLookupResponse,
} from '../../src/onboarding/helpers/onboardingPublicApi'
import {
  ALREADY_IN_ANOTHER_ORG_ERROR,
  DOMAIN_NOT_ALLOWED_ERROR,
  FALLBACK_SIGN_IN_COPY,
  JOIN_CTA_LABELS,
} from '../../appStyles/_joinDeepLink.constants'
import { savePendingB2bJoinCode, clearPendingB2bJoinCode } from '../../src/join/helpers/pendingB2bJoin.helper'
import { getPostB2bOrgJoinPath } from '../../src/join/helpers/b2bOrgJoinRedirect.helper'

/**
 * Max automatic retries covering two failure modes:
 *  1. Transient 5xx (ensureUserExists first-login race on deleted+re-created users)
 *  2. Partial success: HTTP 200 but worldCupJoin.ok === false (autoProvision race beat POST /accounts/join
 *     to create the squad row, or a transient DB contention during the squad upsert)
 * Both share the same counter so total retries stay bounded.
 */
const MAX_JOIN_RETRIES = 2
/** Delay between retries — covers the ~3 s ensureUserExists / squad-join settlement window. */
const JOIN_RETRY_DELAY_MS = 3500

interface ApiEnvelope<T> {
  success: boolean
  error?: string
  data?: T
}

function parseJoinCodeFromParams(raw: string | string[] | undefined): string {
  const first = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : ''
  return sanitizeJoinCodeInput((first ?? '').trim())
}

export default function PublicJoinRoute() {
  const params = useLocalSearchParams<{ join?: string | string[] }>()
  const router = useRouter()
  const appTheme = useTheme()
  const styles = useMemo(() => getJoinDeepLinkStyles(appTheme), [appTheme])
  const { isSignedIn, isLoaded, isAnonymous } = useSupabaseAuth()
  const apiClient = useApiClient()

  const code = useMemo(() => parseJoinCodeFromParams(params.join), [params.join])

  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const joinStartedRef = useRef(false)
  /** Retry counter for transient 5xx — reset on success or permanent error. */
  const retryCountRef = useRef(0)
  /** Stable ref so the retry setTimeout always calls the latest closure. */
  const performJoinRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const [orgData, setOrgData] = useState<PublicOrgLookupResponse | null>(null)
  const [orgResolving, setOrgResolving] = useState(false)
  const orgFetchedRef = useRef(false)

  /** Anonymous Supabase sessions cannot join orgs — treat same as signed-out. */
  const hasRealAccount = isSignedIn && !isAnonymous

  const performJoin = useCallback(async () => {
    if (joinStartedRef.current) return
    joinStartedRef.current = true
    setBusy(true)
    setError(null)
    try {
      const res = await apiClient<ApiEnvelope<B2bJoinOrganizationResponse>>(
        API_ENDPOINTS.accounts.join,
        { method: 'POST', data: { joinCode: code } },
      )
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Could not join')
      }
      // Org joined. If the linked squad join failed (worldCupJoin.ok === false), retry silently
      // once — the second call finds the org membership already ACTIVE and re-attempts only the
      // squad join, which usually succeeds once autoProvision / ensureUserExists has settled.
      // After MAX_JOIN_RETRIES or if worldCupJoin is null (no linked squad), fall through and
      // navigate to the generic groups page.
      const squadOk = res.data.data?.worldCupJoin == null || res.data.data.worldCupJoin.ok === true
      if (!squadOk && retryCountRef.current < MAX_JOIN_RETRIES) {
        retryCountRef.current += 1
        joinStartedRef.current = false
        logger.warn('[PublicJoinRoute] squad join failed, retrying', { attempt: retryCountRef.current })
        setTimeout(() => { void performJoinRef.current() }, JOIN_RETRY_DELAY_MS)
        return
      }
      retryCountRef.current = 0
      await clearPendingB2bJoinCode()
      router.replace(getPostB2bOrgJoinPath(res.data.data) as Href)
    } catch (e) {
      logger.warn('[PublicJoinRoute] join failed', { message: e instanceof Error ? e.message : String(e) })
      // Silent auto-retry for transient 5xx (ensureUserExists first-login race).
      if (isTransientServerError(e) && retryCountRef.current < MAX_JOIN_RETRIES) {
        retryCountRef.current += 1
        joinStartedRef.current = false
        setTimeout(() => { void performJoinRef.current() }, JOIN_RETRY_DELAY_MS)
        return
      }
      retryCountRef.current = 0
      setError(extractErrorMessage(e, 'Could not join'))
      joinStartedRef.current = false
      setBusy(false)
    }
  }, [apiClient, code, router])
  performJoinRef.current = performJoin

  // Persist join intent before sign-in so OAuth can land on /app/ and still complete join.
  useEffect(() => {
    if (!code || code.length < 4) return
    void savePendingB2bJoinCode(code)
  }, [code])

  useEffect(() => {
    if (!isLoaded) return
    if (!hasRealAccount) return
    if (!code || code.length < 4) return
    void performJoin()
  }, [isLoaded, hasRealAccount, code, performJoin])

  // Signed-out: fetch org branding so the landing page shows org info
  useEffect(() => {
    if (!isLoaded) return
    if (hasRealAccount) return
    if (!code || code.length < 4) return
    if (orgFetchedRef.current) return
    orgFetchedRef.current = true
    setOrgResolving(true)
    fetchPublicOrgByJoinCode(code)
      .then(setOrgData)
      .catch((e) => logger.warn('[PublicJoinRoute] org preview fetch failed', { message: String(e) }))
      .finally(() => setOrgResolving(false))
  }, [isLoaded, hasRealAccount, code])

  if (!isLoaded || orgResolving) {
    return <JoinFlowLoadingScreen />
  }

  if (!code || code.length < 4) {
    return (
      <JoinFlowScreen variant="loading">
        <Text style={styles.errorTitle}>Invalid invite link</Text>
        <Text style={styles.errorMessage}>This link is missing a join code.</Text>
        <Pressable
          onPress={() => router.replace('/app/' as Href)}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>Back to app</Text>
        </Pressable>
      </JoinFlowScreen>
    )
  }

  // Signed-out: show org branded TicketStub landing when we have a slug
  if (!hasRealAccount) {
    const returnUrl = `/join?join=${encodeURIComponent(code)}`
    if (orgData?.slug) {
      return (
        <OrgBrandedLanding
          slug={orgData.slug}
          overrideReturnUrl={returnUrl}
        />
      )
    }
    // Fallback: couldn't resolve org info — show minimal sign-in prompt
    return (
      <JoinFlowScreen variant="loading">
        <Text style={styles.errorTitle}>{FALLBACK_SIGN_IN_COPY.TITLE}</Text>
        <Text style={styles.errorMessage}>{FALLBACK_SIGN_IN_COPY.MESSAGE}</Text>
        <Pressable
          onPress={() => router.replace(buildPublicSignInHref({ signup: true, returnUrl }) as Href)}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>{JOIN_CTA_LABELS.CREATE_ACCOUNT}</Text>
        </Pressable>
        <Pressable
          onPress={() => router.replace(buildPublicSignInHref({ returnUrl }) as Href)}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            styles.buttonSpacedTop,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>{JOIN_CTA_LABELS.SIGN_IN}</Text>
        </Pressable>
      </JoinFlowScreen>
    )
  }

  if (error) {
    const isConflict = error === ALREADY_IN_ANOTHER_ORG_ERROR
    const isDomainRestricted = error === DOMAIN_NOT_ALLOWED_ERROR

    if (isDomainRestricted) {
      return (
        <JoinFlowScreen>
          <View style={styles.container}>
            <View style={styles.card}>
              <View style={styles.iconCircle}>
                <Text style={styles.iconText}>🔒</Text>
              </View>
              <Text style={styles.title}>Members only</Text>
              <Text style={styles.subtitle}>
                This organisation is restricted to company email accounts.
              </Text>
              <Text style={styles.description}>
                Sign in with your work email address to join, or contact your organisation admin for access.
              </Text>
              <Pressable
                onPress={() => router.replace('/app/' as Href)}
                style={({ pressed, hovered }) => [
                  styles.primaryButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.primaryButtonPressed,
                  hovered && Platform.OS === 'web' && styles.primaryButtonHover,
                ]}
              >
                <Text style={styles.primaryButtonText}>{JOIN_CTA_LABELS.BACK_TO_APP}</Text>
              </Pressable>
            </View>
          </View>
        </JoinFlowScreen>
      )
    }

    return (
      <JoinFlowScreen variant="loading">
        <Text style={styles.errorTitle}>{error}</Text>
        {!isConflict && (
          <Pressable
            onPress={() => {
              joinStartedRef.current = false
              void performJoin()
            }}
            style={({ pressed, hovered }) => [
              styles.goHomeButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.goHomeButtonPressed,
              hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
            ]}
          >
            <Text style={styles.goHomeButtonText}>{JOIN_CTA_LABELS.TRY_AGAIN}</Text>
          </Pressable>
        )}
        <Pressable
          onPress={() => router.replace('/app/' as Href)}
          style={({ pressed, hovered }) => [
            styles.goHomeButton,
            styles.buttonSpacedTop,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.goHomeButtonPressed,
            hovered && Platform.OS === 'web' && styles.goHomeButtonHover,
          ]}
        >
          <Text style={styles.goHomeButtonText}>{JOIN_CTA_LABELS.BACK_TO_APP}</Text>
        </Pressable>
      </JoinFlowScreen>
    )
  }

  return (
    <JoinFlowLoadingScreen message={busy ? 'Joining your squad…' : 'Loading…'} />
  )
}
